import { Dialog } from "construct-ui";
import DatePickerComponent from "../../../components/date-picker/date-picker.component";
import SingleSelectComponent from "../../../components/single-select/single-select.component";
import { formats } from "../../../config/constants";
import loginModel from "../../../models/login.model";
import RegistrationSingleChannelExcelModel from "../../../models/registration/single-channel-excel/single-channel-excel.model";
import siteModel from "../../../models/site/site.model";
import ViewBase from "../../view-base.view";
import SMEDealerPrivacyPolicyView from "../privacy-policy/sme-incentive-dealer.view";
import SMEPrivacyPolicyRAView from "../privacy-policy/sme-incentive-ra.view";
import moment from "moment";

/**
 * Registration Single Channel Excel View
 * 
 * A page to upload Single Channel Registration by excel
 * 
 * @author cmcnkhiep(Ryan)
 */

export default function SingleChannelExcel() {
    ViewBase.call(this);

    const BOOTSTRAP_INVALID_CLASS = 'is-invalid';
    const ACCEPTED_FILE_FORMAT = ['.xls', '.xlsx'];
    const FORM_ID = 'registrationExcelSingleUploadForm';
    const UPLOAD_INPUT_ID = 'registrationExcelSingleUploadInput';
    const ROLES_TYPE = {
        DEALER: "DEALER",
        RA: "RA",
        NONE: "NONE"
    }
    const MALAYSIA_CODE = "MY";

    let managedSiteList = [];
    let backDateLimitDate = '';
    let consentInfo = null;

    let selectedManagedSite = { siteCode: '' };
    let selectedSaleDate = moment().format(formats.dateFormat);
    let uploadResult = null;

    let errorMessage = '';
    let isLegalClauseDialogOpen = false;
    let acceptPolicy = false;

    const isMalaysia = loginModel.credentials.country.toUpperCase() === MALAYSIA_CODE;

    const getManagedSite = () => {
        this.showLoading();
        siteModel.getManagedSite()
            .then((result) => {
                managedSiteList = result;
            })
            .catch((error) => {
                this.handleError(error);
            })
            .finally(() => {
                this.hideLoading();
            })
    }

    const getUserBackDateLimitDate = () => {
        this.showLoading();
        RegistrationSingleChannelExcelModel.getUserBackDateLimitDate()
            .then((result) => {
                if (result) {
                    backDateLimitDate = result.date;
                }
            })
            .catch((error) => {
                this.handleError(error);
            })
            .finally(() => {
                this.hideLoading();
            })
    }

    const downloadResultFile = (fileId, fileName) => {
        RegistrationSingleChannelExcelModel.downloadResultFile(fileId, fileName)
            .catch((error) => {
                this.handleError(error)
            })
    }

    const upload = () => {
        this.showLoading();
        let excelRegistration = document.querySelector(`#${UPLOAD_INPUT_ID}`).files[0];
        RegistrationSingleChannelExcelModel.uploadRegistrationExcelSingle({
            site: selectedManagedSite.siteCode,
            saleDate: moment(selectedSaleDate).format(formats.apiDateFormat),
            excelRegistration,
            legalYN: isMalaysia ? "Y" : "N"
        })
            .then((result) => {
                if (result) {
                    uploadResult = result
                }
                isLegalClauseDialogOpen = false;
            })
            .catch((error) => {
                console.log(error);
                this.handleError(error);
            })
            .finally(() => {
                this.hideLoading();
            });
    }

    const validate = () => {
        // Form validation
        if (!this.validateForm()) {
            errorMessage = this.getText('EM00000316', 'Please select a file');
            return false;
        }

        let fileUploadInput = document.querySelector(`#${UPLOAD_INPUT_ID}`);
        if (ACCEPTED_FILE_FORMAT.some(format => fileUploadInput.files[0].name.endsWith(format))) {
            // in the event where previously was incorrect and it's been corrected, remove the invalid class
            fileUploadInput.classList.remove(BOOTSTRAP_INVALID_CLASS);
            return true;
        }

        fileUploadInput.classList.add(BOOTSTRAP_INVALID_CLASS);
        errorMessage = this.getText('EM00000058', 'Invalid file.');
        return false;
    }

    const handleUploadButtonClick = (e) => {
        e.preventDefault();
        if (!validate()) {
            e.stopPropagation();
            return;
        }

        if (!selectedManagedSite?.siteCode) {
            this.showErrorMessage(
                this.getText("LT00000903", "Please check required fields"),
                this.getText("EM00000108", "Validation error")
            );
            return;
        }

        if (isMalaysia) {
            this.showLoading();

            RegistrationSingleChannelExcelModel.getConsentInfo()
                .then((consentRes) => {
                    consentInfo = consentRes;
                    const isUserTypeNONE = consentRes?.userType?.toLowerCase() === ROLES_TYPE.NONE.toLowerCase();
                    if (isUserTypeNONE) {
                        upload();
                    } else {
                        isLegalClauseDialogOpen = true;
                    }
                })
                .catch((error) => {
                    this.handleError(error);
                })
                .finally(() => {
                    this.hideLoading();
                });

        } else {
            upload();
        }

    };

    const handleConfirmPolicyClick = () => {
        if (!acceptPolicy) {
            return;
        }
        upload();
    }

    const downloadTemplate = () => {
        RegistrationSingleChannelExcelModel.downloadExcelTemplate()
            .catch((error) => {
                this.handleError(error)
            })
    }

    const isDealerOrRA = () => consentInfo?.userType?.toLowerCase() !== ROLES_TYPE.NONE.toLowerCase();
    const isValidToRenderPrivacyPopup = () => isMalaysia && isDealerOrRA;
    const isDealer = () => consentInfo?.userType?.toLowerCase() === ROLES_TYPE.DEALER.toLowerCase();
    const isRA = () => consentInfo?.userType?.toLowerCase() === ROLES_TYPE.RA.toLowerCase();

    const handleChangeAccept = (e) => {
        acceptPolicy = e.target.checked;
    }

    return {
        oninit: () => {
            getManagedSite();
            getUserBackDateLimitDate();
        },
        view: () => {
            return [
                /* TABLE */
                m('.card',
                    m('.card-body.g-2',
                        m('.row.g-2',
                            (loginModel.credentials.country === 'MY' &&
                                m('p.h5.fw-bold.text-danger',
                                    this.getText('LT00000916', '*For HHP products, end consume phone number is required to pay for incentive. Samsung terms and conditions apply. You can upload max 300 lines at one time')
                                )
                            ),
                            m('p.h5.fw-bold.col-12',
                                this.getText('LT00000917', 'Cannot register Sales Date before'),
                                ' ',
                                m('span.text-danger', backDateLimitDate)
                            ),
                            m('label.col-2.col-form-label.h5.fw-bold[for="site"]', this.getText("LT00000071", "Site")),
                            m('.col-3',
                                m(SingleSelectComponent,
                                    {
                                        itemKey: 'siteCode',
                                        itemValue: 'siteName',
                                        items: managedSiteList,
                                        selectedItem: selectedManagedSite,
                                        onSelect: (selected) => {
                                            selectedManagedSite = selected;
                                        }
                                    }
                                ),
                            ),
                            m('.w-100'),
                            m('label.col-2.col-form-label.h5.fw-bold[for="sales-date"]', this.getText("LT00000918", "Sales Date")),
                            m('.col-3',
                                backDateLimitDate && m(DatePickerComponent, {
                                    class: 'form-control',
                                    minDate: backDateLimitDate,
                                    date: selectedSaleDate,
                                    format: formats.dateFormat,
                                    onselect: (e) => {
                                        selectedSaleDate = moment(e.detail.date).format(formats.dateFormat)
                                    }
                                })
                            ),
                            m('.w-100'),
                            m('label.col-2.col-form-label.h5.fw-bold',
                                { for: FORM_ID },
                                this.getText("LT00000493", "Excel")
                            ),
                            m('form.col-3.position-relative.needs-validation[novalidate]', { id: FORM_ID, onsubmit: handleUploadButtonClick },
                                m('input.form-control', { id: UPLOAD_INPUT_ID, type: 'file', accept: ACCEPTED_FILE_FORMAT.toString(), required: true }),
                                m('.invalid-tooltip', errorMessage),
                            ),
                            m('.col-1',
                                m('button.btn.btn-primary[type="submit"]',
                                    { form: FORM_ID },
                                    this.getText('LT00000290', 'Upload')
                                )
                            ),
                            m('.col-auto',
                                m('button.btn.btn-primary', {
                                    onclick: downloadTemplate
                                }, m('i.bi.bi-file-earmark-arrow-down-fill'), this.getText('LT00000220', 'Excel Template'))
                            ),
                            m('.w-100'),
                            uploadResult &&
                            [
                                m('p.h5.fw-bold',
                                    this.getText('LT00000794', 'Total record(s) inserted'),
                                    m('span.px-4', uploadResult.totalInsertedRecord),
                                    m('a.cursor-pointer', {
                                        onclick: () => { downloadResultFile(uploadResult.successFileGUID, "RegistrationSingleChannelSuccess") }
                                    }, this.getText('LT00000911', 'Download inserted record(s)'))
                                ),
                                m('p.h5.fw-bold.g-2',
                                    this.getText('LT00000795', 'Total record(s) rejected'),
                                    m('span.text-danger.px-4', uploadResult.totalFailRecord),
                                    m('a.cursor-pointer', {
                                        onclick: () => { downloadResultFile(uploadResult.rejectedFileGUID, "RegistrationSingleChannelRejected") }
                                    }, this.getText('LT00000912', 'Download rejected record(s)'))
                                )
                            ],
                        ),

                        m('.row.g-2',
                            m('.col-12',
                                m('.table-responsive',
                                    m('table.table.table-hover.sticky-column-start',
                                        m('thead',
                                            m('tr.align-middle',
                                                m('th', this.getText('LT00000908', 'IMEI')),
                                                m('th', this.getText('LT00000717', 'Model')),
                                                m('th', this.getText('LT00000909', 'End User Name')),
                                                m('th', this.getText('LT00000910', 'End User Phone')),
                                                m('th', this.getText('LT00000117', 'Type')),
                                                m('th', this.getText('LT00000015', 'Status')),
                                            )
                                        ),
                                        m('tbody',
                                            uploadResult?.failRecords.length > 0
                                                ? uploadResult.failRecords.map((data, index) =>
                                                    m('tr.align-middle',
                                                        m('td', m('span.cursor-text', data.imei)),
                                                        m('td', m('span.cursor-text', data.modelCd)),
                                                        m('td', m('span.cursor-text', data.userName)),
                                                        m('td', m('span.cursor-text', data.userPhone)),
                                                        m('td', m('span.cursor-text', data.type)),
                                                        m('td', m('span.cursor-text', data.status)),
                                                    )
                                                )
                                                : m('tr.align-middle',
                                                    m('td[colspan=6]', this.getText("EM00000141", "No records found."))
                                                ),
                                        )
                                    )
                                )
                            ),
                        )
                    )
                ),
                isValidToRenderPrivacyPopup() &&
                m(Dialog, {
                    class: "cui-dialog-xl",
                    isOpen: isLegalClauseDialogOpen,
                    onClose: () => { isLegalClauseDialogOpen = false; acceptPolicy = false; },
                    title: this.getText("LT00000913", "SME Legal Clause"),
                    content: [
                        isDealer() && m(SMEDealerPrivacyPolicyView, {
                            onChangeAccept: handleChangeAccept,
                        }),
                        isRA() && m(SMEPrivacyPolicyRAView, {
                            onChangeAccept: handleChangeAccept,
                        }),
                        this.getBaseViews(),
                    ],
                    footer: m(".text-end", m('button.btn.btn-primary', { onclick: handleConfirmPolicyClick, disabled: !acceptPolicy }, this.getText("LT00000005", "Submit")))
                }),
                this.getBaseViews()
            ];
        }
    };
}

